import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import { effectivePrice, calcVolume, formatEur, tierPrice } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Plus, Minus, Trash2, Copy, Pencil, StickyNote, Calculator, Tag, ChevronDown, Search, X, Sparkles } from "lucide-react";
import RitiroSection from "./RitiroSection";
import SmontaggioSection from "./SmontaggioSection";
import ImballaggioSection from "./ImballaggioSection";
import PackingSection from "./PackingSection";
import SelfStorageComparison from "./SelfStorageComparison";
import MarketVerifySection from "./MarketVerifySection";
import { PricePerM3Card } from "./PricePerM3Card";
import { PriceGauge } from "./PriceGauge";
import { CatalogPicker } from "./CatalogPicker";

const CATEGORIES = [
  { key: "mobili", label: "Mobili" },
  { key: "elettrodomestici", label: "Elettrodomestici" },
  { key: "scatole", label: "Scatole" },
  { key: "altro", label: "Altro" },
];

function CatalogAdder() {
  const { catalog, items, addCatalogItem, setItemQty, discounts } = useQuote();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const qtyOf = (label) => items.filter((i) => i.tipo === label).reduce((s, i) => s + i.qty, 0);

  const categories = React.useMemo(() => {
    const seen = [];
    catalog.forEach((c) => { if (!seen.includes(c.category)) seen.push(c.category); });
    return seen;
  }, [catalog]);

  const q = search.trim().toLowerCase();
  const norm = (s) => (s || "").toLowerCase();
  const tokens = q.split(/\s+/).filter(Boolean);
  const matches = q ? catalog.filter((c) => norm(c.label).includes(q)) : [];
  const matchSet = new Set(matches.map((m) => m.label));
  const similar = q
    ? catalog.filter((c) => !matchSet.has(c.label) && tokens.some((tk) => tk.length >= 3 && norm(c.label).includes(tk))).slice(0, 8)
    : [];

  if (categories.length === 0) return null;

  const CatCard = (it) => {
    const cnt = qtyOf(it.label);
    const inQuote = items.find((i) => i.tipo === it.label);
    return (
      <div key={it.label} className="border border-slate-200 rounded-md p-3 flex items-center justify-between gap-2 hover:border-[#1a5f5a]/40 transition-colors">
        <div className="min-w-0">
          <div className="font-medium text-sm text-slate-800 truncate">{it.label}</div>
          <div className="text-xs text-slate-400">€{formatEur(it.price_eur)}/mese · {it.length_cm}×{it.width_cm}×{it.height_cm}cm</div>
        </div>
        {cnt > 0 ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="icon" variant="outline" className="h-7 w-7" data-testid={`cat-minus-${it.label}`}
              onClick={() => setItemQty(inQuote.id, inQuote.qty - 1)}><Minus className="h-3.5 w-3.5" /></Button>
            <span className="w-5 text-center text-sm font-semibold">{cnt}</span>
            <Button size="icon" className="h-7 w-7 bg-[#1a5f5a] hover:bg-[#134744]" data-testid={`cat-plus-${it.label}`}
              onClick={() => addCatalogItem(it)}><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        ) : (
          <Button size="icon" className="h-8 w-8 bg-[#1a5f5a] hover:bg-[#134744] shrink-0" data-testid={`cat-add-${it.label}`}
            onClick={() => addCatalogItem(it)}><Plus className="h-4 w-4" /></Button>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="catalog-adder">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between" data-testid="catalog-adder-toggle">
        <h3 className="font-display font-semibold text-lg">Aggiungi oggetti dal catalogo</h3>
        <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
      <div className="mt-4">
        <div className="relative mb-4">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca un oggetto nel catalogo…"
            className="pl-9 pr-9" data-testid="catalog-search-input" />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="catalog-search-clear">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {q ? (
          <div className="space-y-4" data-testid="catalog-search-results">
            {matches.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">Risultati ({matches.length})</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{matches.map(CatCard)}</div>
              </div>
            )}
            {similar.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">{matches.length > 0 ? "Oggetti simili" : "Forse cercavi"}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{similar.map(CatCard)}</div>
              </div>
            )}
            {matches.length === 0 && similar.length === 0 && (
              <p className="text-sm text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-md" data-testid="catalog-search-empty">Nessun oggetto trovato per "{search}".</p>
            )}
          </div>
        ) : (
        <Tabs defaultValue={categories[0]}>
          <TabsList className="mb-4 flex-wrap h-auto justify-start gap-1">
            {categories.map((c) => <TabsTrigger key={c} value={c} data-testid={`cat-tab-${c}`} className="text-xs">{c}</TabsTrigger>)}
          </TabsList>
          {categories.map((c) => (
            <TabsContent key={c} value={c}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {catalog.filter((x) => x.category === c).map(CatCard)}
              </div>
            </TabsContent>
          ))}
        </Tabs>
        )}
      </div>
      )}
    </div>
  );
}

function ItemRow({ it }) {
  const { updateItem, setItemQty, removeItem, duplicateItem, discounts, reassignCatalog, catalog } = useQuote();
  const [showNote, setShowNote] = useState(!!it.note);
  const [override, setOverride] = useState(it.isPriceOverridden || it.isDimOverridden);
  const price = effectivePrice(it, discounts);
  const vol = calcVolume(it.dims.l, it.dims.w, it.dims.h);
  const hasDiscount = !it.isPriceOverridden && tierPrice(it.tipo, it.qty, discounts) != null;
  const pricePerM3 = vol > 0 ? price / vol : 0;

  return (
    <div className="border border-slate-200 rounded-md p-4 bg-white" data-testid={`quote-item-${it.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <Input value={it.tipo} onChange={(e) => updateItem(it.id, { tipo: e.target.value })}
            className="font-medium border-0 px-0 h-7 focus-visible:ring-0 shadow-none text-slate-800" data-testid={`item-name-${it.id}`} />
          <CatalogPicker
            value={it.fromCatalog && catalog.some((c) => c.label === it.tipo) ? it.tipo : null}
            testid={`item-catalog-picker-${it.id}`}
            onSelect={(c) => c ? reassignCatalog(it.id, c) : updateItem(it.id, { fromCatalog: false })}
          />
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {it.qty > 1 ? (
              <span data-testid={`item-volume-${it.id}`}>{vol.toFixed(3)} m³/pz · {(vol * it.qty).toFixed(2)} m³ tot</span>
            ) : (
              <span data-testid={`item-volume-${it.id}`}>{vol.toFixed(3)} m³</span>
            )}
            <span className="text-[#1a5f5a] font-medium" data-testid={`item-price-per-m3-${it.id}`}>· €{formatEur(pricePerM3)}/m³</span>
            <span>· {((it.fachMinutes || 0) * it.qty)} min facch.</span>
            {(it.smontMinutes ?? it.imbMinutes ?? 0) > 0 && it.includeSmont !== false && <span>· {((it.smontMinutes ?? it.imbMinutes ?? 0) * it.qty)} min smont.</span>}
            {it.imbMinutes > 0 && it.includeImb !== false && <span>· {(it.imbMinutes * it.qty)} min imb.</span>}
            {hasDiscount && <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 text-[10px] gap-1"><Tag className="h-3 w-3" /> Sconto volume</Badge>}
            {it.isDimEstimated && <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100 text-[10px] gap-1" data-testid={`item-dim-estimated-${it.id}`}><Sparkles className="h-3 w-3" /> Dimensioni stimate</Badge>}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => setOverride(!override)} data-testid={`item-override-${it.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => setShowNote(!showNote)}><StickyNote className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-slate-400" onClick={() => duplicateItem(it.id)}><Copy className="h-3.5 w-3.5" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-500" onClick={() => removeItem(it.id)} data-testid={`item-remove-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      <div className="flex items-center justify-between mt-3 gap-3">
        <div className="flex items-center gap-1.5">
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setItemQty(it.id, it.qty - 1)} data-testid={`item-minus-${it.id}`}><Minus className="h-3.5 w-3.5" /></Button>
          <span className="w-6 text-center text-sm font-semibold">{it.qty}</span>
          <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setItemQty(it.id, it.qty + 1)} data-testid={`item-plus-${it.id}`}><Plus className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-sm">
            <span className="text-slate-400">€</span>
            <Input type="number" step="0.5" value={Number(price).toFixed(2)}
              onChange={(e) => updateItem(it.id, { pricePerMonth: parseFloat(e.target.value) || 0, isPriceOverridden: true })}
              className="w-20 h-8 text-right" data-testid={`item-price-${it.id}`} />
            <span className="text-slate-400 text-xs">/mese</span>
          </div>
          <span className="text-sm font-semibold text-[#1a5f5a] w-20 text-right">€{formatEur(price * it.qty)}</span>
        </div>
      </div>

      {override && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {["l", "w", "h"].map((d) => (
              <div key={d}>
                <label className="text-[10px] uppercase text-slate-400">{d === "l" ? "Lung." : d === "w" ? "Larg." : "Alt."} (cm)</label>
                <Input type="number" value={it.dims[d]} onChange={(e) => updateItem(it.id, { dims: { ...it.dims, [d]: parseFloat(e.target.value) || 0 }, isDimOverridden: true, isDimEstimated: false })} className="h-8" data-testid={`item-dim-${d}-${it.id}`} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-2">
            <div>
              <label className="text-[10px] uppercase text-slate-400">Facchinaggio (min/unità)</label>
              <Input type="number" value={it.fachMinutes ?? 0} onChange={(e) => updateItem(it.id, { fachMinutes: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`item-fach-${it.id}`} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase text-slate-400">Smontaggio (min/unità)</label>
                <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                  <input type="checkbox" checked={it.includeSmont !== false} onChange={(e) => updateItem(it.id, { includeSmont: e.target.checked })} data-testid={`item-include-smont-${it.id}`} />
                  Includi
                </label>
              </div>
              <Input type="number" value={it.smontMinutes ?? it.imbMinutes ?? 0} onChange={(e) => updateItem(it.id, { smontMinutes: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`item-smont-${it.id}`} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] uppercase text-slate-400">Imballaggio (min/unità)</label>
                <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer">
                  <input type="checkbox" checked={it.includeImb !== false} onChange={(e) => updateItem(it.id, { includeImb: e.target.checked })} data-testid={`item-include-imb-${it.id}`} />
                  Includi
                </label>
              </div>
              <Input type="number" value={it.imbMinutes ?? 0} onChange={(e) => updateItem(it.id, { imbMinutes: parseFloat(e.target.value) || 0 })} className="h-8" data-testid={`item-imb-${it.id}`} />
            </div>
          </div>
        </div>
      )}

      {showNote && (
        <Input value={it.note} onChange={(e) => updateItem(it.id, { note: e.target.value })}
          placeholder="Nota (opzionale)" className="mt-3 h-8 text-sm" data-testid={`item-note-${it.id}`} />
      )}
    </div>
  );
}

function TotalCard() {
  const { items, monthlyTotal, totalVolume, isMonthlyTotalManual, setManualTotal, resetManualTotal, distributeTotal } = useQuote();
  const [showManual, setShowManual] = useState(false);
  const [target, setTarget] = useState("");
  const costPerM3 = totalVolume > 0 ? monthlyTotal / totalVolume : 0;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6" data-testid="total-card">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400">Totale mensile</div>
          <div className="text-3xl font-display font-semibold text-[#1a5f5a] mt-1" data-testid="monthly-total">€{formatEur(monthlyTotal)}<span className="text-base text-slate-400 font-normal">/mese</span></div>
        </div>
        <div className="text-right text-sm text-slate-500">
          <div>{totalVolume.toFixed(2)} m³</div>
          <div className="text-xs text-slate-400">€{formatEur(costPerM3)}/m³</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <Button variant="outline" size="sm" onClick={() => setShowManual(!showManual)} data-testid="manual-total-button" className="gap-1.5">
          <Calculator className="h-4 w-4" /> Imposta totale manuale
        </Button>
        {isMonthlyTotalManual && <Button variant="ghost" size="sm" onClick={resetManualTotal} className="text-slate-400">Reset</Button>}
      </div>

      {showManual && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <Input type="number" placeholder="Totale €" value={target} onChange={(e) => setTarget(e.target.value)} className="w-32 h-9" data-testid="manual-total-input" />
          <Button size="sm" onClick={() => { setManualTotal(parseFloat(target) || 0); setShowManual(false); }} className="bg-[#1a5f5a] hover:bg-[#134744]">Imposta totale</Button>
          <Button size="sm" variant="outline" onClick={() => { distributeTotal(parseFloat(target) || 0); setShowManual(false); }} data-testid="distribute-total-button">Distribuisci totale</Button>
        </div>
      )}
    </div>
  );
}

export default function ObjectsWorkArea() {
  const { items, mode, monthlyTotal, totalVolume, addCustomItem, imagePhotos } = useQuote();
  const grouped = imagePhotos && imagePhotos.length > 0;

  const renderItems = () => {
    if (items.length === 0) {
      return <p className="text-slate-400 text-sm py-4 text-center border border-dashed border-slate-200 rounded-md">Nessun oggetto aggiunto.</p>;
    }
    if (!grouped) {
      return <div className="space-y-3">{items.map((it) => <ItemRow key={it.id} it={it} />)}</div>;
    }
    const idx = {};
    imagePhotos.forEach((p, i) => { idx[p.id] = i + 1; });
    const groups = imagePhotos.map((p, i) => ({ key: p.id, label: `Foto ${i + 1}`, list: items.filter((it) => it.photoId === p.id) }));
    const others = items.filter((it) => !it.photoId || !idx[it.photoId]);
    return (
      <div className="space-y-5">
        {groups.filter((g) => g.list.length > 0).map((g) => (
          <div key={g.key} data-testid={`item-group-${g.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#1a5f5a] bg-[#1a5f5a]/10 px-2 py-0.5 rounded">{g.label}</span>
              <span className="text-xs text-slate-400">{g.list.length} ogg.</span>
            </div>
            <div className="space-y-3">{g.list.map((it) => <ItemRow key={it.id} it={it} />)}</div>
          </div>
        ))}
        {others.length > 0 && (
          <div data-testid="item-group-altri">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-100 px-2 py-0.5 rounded">Altri oggetti</span>
              <span className="text-xs text-slate-400">{others.length} ogg.</span>
            </div>
            <div className="space-y-3">{others.map((it) => <ItemRow key={it.id} it={it} />)}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5 fade-in">
      <CatalogAdder />

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-lg">Oggetti nel preventivo</h3>
          <Button variant="outline" size="sm" onClick={addCustomItem} className="gap-1.5" data-testid="add-custom-item">
            <Plus className="h-4 w-4" /> Oggetto personalizzato
          </Button>
        </div>
        {renderItems()}
      </div>

      <TotalCard />
      <PricePerM3Card total={monthlyTotal} volume={totalVolume} />
      <RitiroSection />
      <SmontaggioSection />
      <ImballaggioSection />
      <PackingSection />
      <PriceGauge />
      <SelfStorageComparison />
      <MarketVerifySection
        area={Math.round((totalVolume / 0.25 / 2.5) * 10) / 10}
        editableArea
        endpoint="/ai/box-rental-market"
        storeKey="boxRental"
        title="Verifica il mercato"
        description="Trova box / garage in affitto vicini alla metratura indicata (±10%) con canone mensile nella città scelta."
        testidPrefix="box-rental"
        icon="store"
      />
    </div>
  );
}
